import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Stack,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material'
import Header from 'components/Header'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import { useState, useMemo, useRef, useEffect } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { syncQTESSOrders } from 'api/daily-order'
import { v4 as uuidv4 } from 'uuid'
import { useParams } from 'react-router-dom'

type ProgressEventPayload = {
  completed: number
  total: number
  orderNumber?: string
  success?: boolean
  skipped?: boolean
  error?: string | null
}

type LogItem = {
  ts: string
  orderNumber: string
  status: 'success' | 'error' | 'skipped'
  message: string
}

const formatTime = (iso: string) => dayjs(iso).format('HH:mm:ss')

const SyncDailyOrder = () => {
  const sessionIdRef = useRef<string>(uuidv4())
  const sessionId = sessionIdRef.current
  const { company } = useParams()
  const [fromDate, setFromDate] = useState<Dayjs | null>(null)
  const [fromTime, setFromTime] = useState<Dayjs | null>(null)
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs())
  const [toTime, setToTime] = useState<Dayjs | null>(dayjs())
  const [percentage, setPercentage] = useState(0)
  const [completed, setCompleted] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [logs, setLogs] = useState<LogItem[]>([])
  const logEndRef = useRef<HTMLDivElement | null>(null)
  const esRef = useRef<EventSource | null>(null)

  const fromDateTime = useMemo(
    () =>
      fromDate && fromTime
        ? fromDate.set('hour', fromTime.hour()).set('minute', fromTime.minute())
        : null,
    [fromDate, fromTime]
  )
  const toDateTime = useMemo(
    () =>
      toDate && toTime
        ? toDate.set('hour', toTime.hour()).set('minute', toTime.minute())
        : null,
    [toDate, toTime]
  )
  const isRangeValid =
    fromDateTime && toDateTime ? fromDateTime.isBefore(toDateTime) : false

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [logs.length])

  useEffect(() => {
    return () => {
      if (esRef.current) esRef.current.close()
    }
  }, [])

  const appendLog = (li: LogItem) => setLogs(prev => [...prev, li].slice(-1000))

  const handleSubmitSync = async () => {
    if (!isRangeValid || !fromDateTime || !toDateTime) return
    setLogs([])
    setCompleted(0)
    setTotal(0)
    setPercentage(0)
    setLoading(true)
    try {
      await syncQTESSOrders(
        sessionId,
        `${company}`,
        fromDateTime.toISOString(),
        toDateTime.toISOString()
      )
      if (esRef.current) {
        esRef.current.close()
        esRef.current = null
      }
      const eventSource = new EventSource(
        `${process.env.REACT_APP_BASE_SERVER_HOST}/api/daily-order/qtess-order-sync-process?sessionId=${sessionId}`
      )
      esRef.current = eventSource
      eventSource.addEventListener('progress', e => {
        const data = JSON.parse(
          (e as MessageEvent).data
        ) as ProgressEventPayload
        if (
          typeof data.completed === 'number' &&
          typeof data.total === 'number'
        ) {
          setCompleted(data.completed)
          setTotal(data.total)
          setPercentage(Math.round((data.completed / data.total) * 100))
        }
        if (data.orderNumber) {
          const ts = new Date().toISOString()
          if (data.skipped) {
            appendLog({
              ts,
              orderNumber: data.orderNumber,
              status: 'skipped',
              message: 'Skipped (already exists)'
            })
          } else if (data.success) {
            appendLog({
              ts,
              orderNumber: data.orderNumber,
              status: 'success',
              message: 'Synced successfully'
            })
          } else {
            appendLog({
              ts,
              orderNumber: data.orderNumber,
              status: 'error',
              message: data.error || 'Unknown error'
            })
          }
        }
      })
      eventSource.addEventListener('done', e => {
        const data = JSON.parse(
          (e as MessageEvent).data
        ) as ProgressEventPayload
        if (
          typeof data.completed === 'number' &&
          typeof data.total === 'number'
        ) {
          setCompleted(data.completed)
          setTotal(data.total)
          setPercentage(Math.round((data.completed / data.total) * 100))
        }
        eventSource.close()
        esRef.current = null
        setLoading(false)
      })
      eventSource.onmessage = e => {
        const data = JSON.parse(e.data) as ProgressEventPayload
        if (
          typeof data.completed === 'number' &&
          typeof data.total === 'number'
        ) {
          setCompleted(data.completed)
          setTotal(data.total)
          setPercentage(Math.round((data.completed / data.total) * 100))
          if (data.completed === data.total) {
            eventSource.close()
            esRef.current = null
            setLoading(false)
          }
        }
      }
      eventSource.onerror = err => {
        appendLog({
          ts: new Date().toISOString(),
          orderNumber: '-',
          status: 'error',
          message: 'SSE connection error'
        })
        console.error('SSE error', err)
        eventSource.close()
        esRef.current = null
        setLoading(false)
      }
    } catch (err) {
      setLoading(false)
      appendLog({
        ts: new Date().toISOString(),
        orderNumber: '-',
        status: 'error',
        message: 'Failed to start sync'
      })
      console.error('Error syncing QTESS orders:', err)
    }
  }

  return (
    <Box>
      <Header />
      <Container maxWidth='sm'>
        <Paper elevation={3} sx={{ mt: 6, p: 4 }}>
          <Typography variant='h5' gutterBottom>
            Select Sync Time Range
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={4}>
              <Card variant='outlined'>
                <CardContent>
                  <Typography variant='subtitle1' gutterBottom>
                    From
                  </Typography>
                  <Stack spacing={2}>
                    <DatePicker
                      label='From Date'
                      value={fromDate}
                      onChange={val => setFromDate(val)}
                      maxDate={toDate || dayjs()}
                      slotProps={{
                        textField: { fullWidth: true, size: 'small' }
                      }}
                    />
                    <TimePicker
                      label='From Time'
                      value={fromTime}
                      onChange={val => setFromTime(val)}
                      disabled={!fromDate}
                      slotProps={{
                        textField: { fullWidth: true, size: 'small' }
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
              <Card variant='outlined'>
                <CardContent>
                  <Typography variant='subtitle1' gutterBottom>
                    To
                  </Typography>
                  <Stack spacing={2}>
                    <DatePicker
                      label='To Date'
                      value={toDate}
                      onChange={val => setToDate(val || dayjs())}
                      minDate={fromDate || undefined}
                      maxDate={dayjs()}
                      slotProps={{
                        textField: { fullWidth: true, size: 'small' }
                      }}
                    />
                    <TimePicker
                      label='To Time'
                      value={toTime}
                      onChange={val => setToTime(val || dayjs())}
                      disabled={!toDate}
                      slotProps={{
                        textField: { fullWidth: true, size: 'small' }
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
              <Button
                variant='contained'
                size='large'
                fullWidth
                sx={{ mt: 2 }}
                disabled={!isRangeValid || loading}
                onClick={() => {
                  handleSubmitSync()
                }}
              >
                Confirm Sync
              </Button>
              {!isRangeValid && fromDateTime && toDateTime && (
                <Typography color='error' variant='body2'>
                  End time must be after start time.
                </Typography>
              )}
              {total > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant='body1' sx={{ mb: 1 }}>
                    Syncing orders: {completed} / {total} ({percentage}%)
                  </Typography>
                  <LinearProgress variant='determinate' value={percentage} />
                </Box>
              )}
              <Card variant='outlined'>
                <CardContent>
                  <Typography variant='subtitle1' gutterBottom>
                    Sync Logs
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 260,
                      overflowY: 'auto',
                      bgcolor: 'background.default',
                      borderRadius: 1,
                      border: theme => `1px solid ${theme.palette.divider}`
                    }}
                  >
                    <List dense disablePadding>
                      {logs.map((l, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5 }}>
                          <ListItemText
                            primaryTypographyProps={{
                              sx: { fontFamily: 'monospace' }
                            }}
                            primary={`${formatTime(l.ts)}  #${l.orderNumber}  ${l.message}`}
                          />
                          <Chip
                            size='small'
                            label={
                              l.status === 'success'
                                ? 'SUCCESS'
                                : l.status === 'skipped'
                                  ? 'SKIPPED'
                                  : 'ERROR'
                            }
                            color={
                              l.status === 'success'
                                ? 'success'
                                : l.status === 'skipped'
                                  ? 'default'
                                  : 'error'
                            }
                            variant={
                              l.status === 'skipped' ? 'outlined' : 'filled'
                            }
                          />
                        </ListItem>
                      ))}
                      <div ref={logEndRef} />
                    </List>
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          </LocalizationProvider>
        </Paper>
      </Container>
    </Box>
  )
}

export default SyncDailyOrder
